import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Tabs, Tooltip, Select, message, Divider, Tag, Modal } from 'antd';
import { PageContentLayout } from '../../../shared/layout';
import {
  EditOutlined,
  EyeOutlined,
  LeftOutlined,
  LinkOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  UserOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { withRouter, useParams } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import { TaskComments } from './TaskComments';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { statusTypes, urgencyColors } from '../configs/task.config';
import OverlaySpinner from '../../../shared/OverlaySpinner';
import { Histories } from '../../../shared/Histories';
import { debounce, startCase } from 'lodash';
import '../styles/BoardTabs.css';
import '../styles/SelectInput.css';
import { useUserContext } from '../../../../core/components/users/useUserContext';
import { useOrgContext } from '../../../../core/components/orgs/useOrgContext';
import AvatarS3 from '../../../shared/images/AvatarS3';
import { copyTaskLink, hasCampaignsFeature } from '../utils/task.utils';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';
const { Option } = Select;
const { TabPane } = Tabs;

const TaskDetailsView = ({ history }) => {
  const { id: currentTaskId } = useParams();
  const [activeTab, setActiveTab] = useState('1');
  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const windowWidth = useWindowWidth();

  const handleTabChange = (key) => {
    setActiveTab(key);

    if (key === '3' && currentTaskId) {
      fetchHistories({
        variables: {
          where: {
            referenceId: currentTaskId,
            type: 'TASK',
          },
        },
      });
    }
  };

  const {
    currentUser,
    updateTask,
    updateTaskLoading,
    currentTask,
    fetchTask,
    taskLoading,
    fetchHistories,
    histories,
    loadingHistories,
    refetchTask,
  } = useTaskContext();
  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();
  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
    fetchCollaboratorsUsers,
    collaboratorsData,
    collaboratorsLoading,
  } = useUserContext();

  const [selectedOrg, setSelectedOrg] = useState(
    (currentTask && currentTask.assigneeOrg && currentTask.assigneeOrg.id) || null
  );
  const [selectedAssignees, setSelectedAssignees] = useState(
    (currentTask &&
      currentTask.assigneeUsers &&
      currentTask.assigneeUsers[0] &&
      currentTask.assigneeUsers[0].id) ||
      []
  );
  const [selectedCollaborators, setSelectedCollaborators] = useState(
    (currentTask &&
      currentTask.collaborators &&
      currentTask.collaborators.map((user) => user.id)) ||
      []
  );
  const [isFollowers, setIsFollowers] = useState(
    currentTask &&
      currentTask.followers &&
      currentUser &&
      currentTask.followers.some((f) => f.id === currentUser.id)
  );
  const [isCollaborator, setIsCollaborator] = useState(
    currentTask &&
      currentTask.collaborators &&
      currentUser &&
      currentTask.collaborators.some((c) => c.id === currentUser.id)
  );
  const isCurrentUserAssignee =
    currentTask && currentTask.assigneeUsers
      ? currentTask.assigneeUsers.some((user) => user.id === currentUser.id)
      : false;
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);

  useEffect(() => {
    if (currentTask) {
      setSelectedOrg(
        currentTask.assigneeOrg && currentTask.assigneeOrg.id ? currentTask.assigneeOrg.id : null
      );
      setSelectedAssignees(
        currentTask.assigneeUsers
          ? currentTask.assigneeUsers &&
              currentTask.assigneeUsers[0] &&
              currentTask.assigneeUsers[0].id
          : []
      );
      setIsFollowers(
        currentTask.followers &&
          currentUser &&
          currentTask.followers.some((f) => f.id === currentUser.id)
      );
      setIsCollaborator(
        currentTask &&
          currentTask.collaborators &&
          currentUser &&
          currentTask.collaborators.some((c) => c.id === currentUser.id)
      );
      setSelectedCollaborators(
        currentTask && currentTask.collaborators && currentTask.collaborators.length > 0
          ? currentTask.collaborators.map((user) => user.id)
          : []
      );
    }
  }, [currentTask, currentUser]);

  const fetchCollaborators = debounce((name = '') => {
    fetchCollaboratorsUsers({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  OR: [
                    {
                      parentOrg_some: {
                        id: currentUser.role.org.id,
                      },
                    },
                    {
                      id: currentUser.role.org.id,
                    },
                  ],
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchBasicUsers = debounce((name = '') => {
    if (selectedOrg === null) {
      setSelectedAssignees([]);
      return;
    }
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: selectedOrg,
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  useEffect(() => {
    if (currentTask?.project) {
      return;
    }
    if (selectedOrg || isEditingAssignee) {
      fetchBasicUsers();
    } else {
      setSelectedAssignees([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, isEditingAssignee, currentTask?.project]);

  useEffect(() => {
    if (currentTaskId) {
      fetchTask({
        variables: { where: { id: currentTaskId } },
      });
      fetchCollaborators();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTaskId]);

  if (!currentTaskId) return <p>Select a task to see the details.</p>;

  if (taskLoading) {
    return <OverlaySpinner />;
  }

  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  const handleUpdate = async (selectedStatus) => {
    try {
      await updateTask({
        variables: {
          data: {
            status: selectedStatus,
          },
          where: { id: currentTaskId },
        },
      });
      await Promise.all([
        refetchTask(),
        fetchHistories({
          variables: { where: { referenceId: currentTaskId } },
        }),
      ]);
      message.success('Task status updated successfully.');
    } catch (error) {
      message.error('Failed to update task status.');
      console.log('Update failed:', error);
    }
  };

  const handleUserUpdate = async () => {
    try {
      const orgUpdate = selectedOrg
        ? { connect: { id: selectedOrg } }
        : currentTask && currentTask.assigneeOrg
          ? { disconnect: true }
          : {};

      const activityLog = 'Updated Assignee';

      const newAssignee = selectedAssignees;
      const newCollaborators = selectedCollaborators;

      const currentAssignees = currentTask && currentTask.assigneeUsers;
      const currentAssigneeIds = currentAssignees.map((user) => user.id);
      const previousCollaborators =
        currentTask && currentTask.collaborators ? currentTask.collaborators : [];

      const assigneeUsers = {};
      const collaborators = {};
      const followers =
        currentTask && currentTask.followers ? currentTask.followers.map((user) => user.id) : [];

      if (
        previousCollaborators.includes(newAssignee[0]) ||
        newCollaborators.includes(newAssignee[0]) ||
        (selectedAssignees && selectedCollaborators.includes(selectedAssignees))
      ) {
        message.warning(
          'Assignees are already collaborators. Please remove them from collaborators first.'
        );
        return;
      }

      if (selectedAssignees && followers.includes(selectedAssignees)) {
        message.warning(
          'Assignees are already followers. Please remove them from followers first.'
        );
        return;
      }

      if (currentAssignees) {
        const disconnectIds = currentAssigneeIds.filter((id) => !newAssignee.includes(id));
        if (disconnectIds.length > 0) {
          assigneeUsers.disconnect = disconnectIds.map((id) => ({ id }));
        }
      }

      if (newAssignee.length > 0 && !currentAssigneeIds.includes(newAssignee[0])) {
        assigneeUsers.connect = { id: newAssignee };
      }

      const usersToConnect = newCollaborators.filter(
        (id) => !previousCollaborators.some((user) => user.id === id)
      );

      const usersToDisconnect = previousCollaborators.filter(
        (user) => !newCollaborators.includes(user.id)
      );

      if (usersToDisconnect.length > 0) {
        collaborators.disconnect = usersToDisconnect.map((user) => ({
          id: user.id,
        }));
      }

      if (usersToConnect.length > 0) {
        collaborators.connect = usersToConnect.map((id) => ({ id }));
      }

      await updateTask({
        variables: {
          data: {
            assigneeOrg: orgUpdate,
            assigneeUsers,
            collaborators,
            activityLog,
          },
          where: { id: currentTaskId },
        },
      });

      await Promise.all([
        refetchTask(),
        fetchHistories({
          variables: { where: { referenceId: currentTaskId } },
        }),
      ]);
      message.success('Task assignee updated successfully.');
      setIsEditingAssignee(false);
    } catch (error) {
      message.error('Failed to update task assignee.');
      console.log('Update failed:', error);
    }
  };

  const handleCollaboratorsUpdate = async () => {
    if (!isCollaborator && (isCurrentUserAssignee || isFollowers)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees are already collaborators but with more responsibility.');
      } else if (isFollowers) {
        message.warning('Please unfollow first if you want to be a collaborator.');
      }
      return;
    }

    setIsCollaborator(!isCollaborator);
    if (!isCollaborator) {
      try {
        await updateTask({
          variables: {
            data: {
              collaborators: {
                connect: { id: currentUser.id },
              },
            },
            where: { id: currentTaskId },
          },
        });
        refetchTask();
        message.success('You are now a collaborator of the task.');
      } catch (error) {
        message.error('Failed to be a collaborator.');
      }
    } else {
      try {
        await updateTask({
          variables: {
            data: {
              collaborators: {
                disconnect: { id: currentUser.id },
              },
            },
            where: { id: currentTaskId },
          },
        });
        refetchTask();
        message.success('Left the task.');
      } catch (error) {
        message.error('Failed to leave the task.');
      }
    }
  };

  const fetchBasicOrgs = debounce((name = '') => {
    fetchBasicOrgsDetails({
      variables: {
        first: 10,
        skip: 0,
        where: {
          AND: [
            {
              OR: [
                { parentOrg_some: { id: currentUser.role.org.id } },
                { id: currentUser.role.org.id },
              ],
              name_contains: name,
            },
          ],
          OR: [
            {
              roles_some: {
                org: {
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
            },
          ],
        },
      },
    });
  }, 500);

  document.title = currentTask && currentTask.name ? `Task: ${currentTask.name}` : 'Task Details';
  return (
    <div
      style={{
        backgroundColor: 'white',
        paddingInline: windowWidth < 768 ? '4%' : '8%',
        height: '100%',
      }}
    >
      <Row
        style={{
          paddingTop: '10px',
          paddingBottom: '5px',
        }}
      >
        <Button
          style={{ border: 'none', boxShadow: 'none' }}
          onClick={() => {
            const previousPath = document.referrer;
            const cameFromProject = previousPath.includes('/tasks/projects/details/');

            if (currentTask?.project?.id && (cameFromProject || history.length <= 2)) {
              history.push(`/tasks/projects/details/${currentTask.project.id}`);
            } else {
              history.goBack();
            }
          }}
        >
          <LeftOutlined style={{ paddingRight: '10px' }} />
          <Text strong>Back</Text>
        </Button>
      </Row>
      <Row
        style={{
          display: 'flex',
          flexDirection: windowWidth < 768 ? 'column' : 'row',
          alignItems: windowWidth < 768 ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: windowWidth < 768 ? '16px' : '0',
        }}
      >
        <div style={{ flex: 1 }}>
          <Text
            style={{
              fontSize: windowWidth < 768 ? '20px' : '25px',
              fontWeight: 'bolder',
            }}
          >
            {currentTask && currentTask.name}
          </Text>
          {currentTask?.project && (
            <div style={{ marginBlock: 8 }}>
              <Tag
                color="blue"
                style={{
                  fontSize: '13px',
                  padding: '4px 12px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
                onClick={() => history.push(`/tasks/projects/details/${currentTask.project.id}`)}
              >
                <LinkOutlined style={{ marginRight: 4 }} />
                Project: {currentTask.project.name}
              </Tag>
            </div>
          )}
        </div>
        <Col
          style={{
            display: 'flex',
            flexDirection: windowWidth < 1200 ? 'column' : 'row',
            gap: windowWidth < 1200 ? '8px' : '0',
            width: windowWidth < 1200 ? '100%' : 'auto',
            paddingBottom: '10px',
          }}
        >
          {level !== 'CHANNEL_PARTNER' && (
            <>
              <Col style={{ paddingInline: windowWidth < 1200 ? '0' : '5px', width: windowWidth < 1200 ? '100%' : 'auto' }}>
                <Select
                  className="custom-select"
                  style={{ width: windowWidth < 1200 ? '100%' : '180px', height: '45px' }}
                  value={currentTask && currentTask.status}
                  placeholder="Update Status"
                  onChange={handleUpdate}
                  loading={updateTaskLoading || taskLoading}
                  disabled={updateTaskLoading || taskLoading}
                >
                  {Object.keys(statusTypes).map((status) => (
                    <Select.Option key={statusTypes[status]} value={statusTypes[status]}>
                      {status}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  paddingInline: windowWidth < 1200 ? '0' : '5px',
                  width: windowWidth < 1200 ? '100%' : 'auto',
                }}
              >
                <Tooltip title="Copy Task Link">
                  <Button
                    onClick={() => copyTaskLink(currentTaskId, message)}
                    icon={<CopyOutlined />}
                    style={{
                      fontSize: '16px',
                      height: '41px',
                      width: '41px',
                      borderRadius: '5px',
                    }}
                  />
                </Tooltip>
                <Button
                  onClick={() => history.push(`/tasks/form/${currentTaskId}`)}
                  style={{
                    fontSize: '15px',
                    height: '41px',
                    borderRadius: '5px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    width: windowWidth < 1200 ? 'calc(100% - 49px)' : '130px',
                  }}
                >
                  <EditOutlined /> Edit Task
                </Button>
              </div>
            </>
          )}
        </Col>
      </Row>
      {/* <PageHeaderLayout
        title={currentTask && currentTask.name}
        titleIcon={
          !currentTask || !currentTask.urgency ? (
            <CheckCircleOutlined
              style={{
                marginRight: "10px",
                fontSize: 25,
              }}
            />
          ) : (
            <Tooltip
              title={
                currentTask.urgency
                  ? `URGENCY: ${currentTask.urgency}`
                  : undefined
              }
            >
              <Badge
                className="custom-badge"
                style={{
                  marginRight: "10px",
                }}
                color={
                  currentTask ? urgencyColors[currentTask.urgency] : "white"
                }
              />
            </Tooltip>
          )
        }
        titleDescription={
          currentTask && currentTask.status === null
            ? "N/A"
            : currentTask && currentTask.status.replace(/_/g, " ").toUpperCase()
        }
        titleDescriptionStyle={{ marginLeft: "10px" }}
        style={{ height: "80px", border: "none" }}
      /> */}

      {/* Content Layout where tab content is rendered */}
      <PageContentLayout
        style={{
          backgroundColor: 'white',
          display: 'inline-flex',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: windowWidth < 1200 ? 'column' : 'row',
            width: '100%',
            gap: windowWidth < 768 ? '16px' : '20px',
          }}
        >
          <Col style={{ width: '100%', flex: windowWidth < 1200 ? 'none' : 1 }}>
            <Row
              style={{
                backgroundColor: 'white',
                border: 'none',
                paddingTop: '2vh',
                paddingBottom: '1vh',
              }}
            >
              <Tabs
                className="custom-tabs-details-history"
                activeKey={activeTab}
                onChange={handleTabChange}
                type="card"
                tabBarStyle={{
                  border: '4px solid #f1f5f9',
                  borderRadius: '8px',
                  width: 'fit-content',
                  display: 'inline-flex',
                  gap: 0,
                  margin: 0,
                }}
              >
                <TabPane tab="Details" key="1" />
                {currentTask && currentTask.relatedCampaigns && currentTask.relatedCampaigns.length > 0 && hasCampaignsFeature(currentUser) && (
                  <TabPane tab="Campaigns" key="2" />
                )}
                <TabPane tab="History" key="3" />
              </Tabs>
            </Row>
            {activeTab === '1' && (
              <Row style={{ justifyContent: 'center' }}>
                <Col
                  style={{
                    width: '100%',
                    marginRight: '20px',
                    paddingBottom: windowWidth < 1200 ? '20px' : '20%',
                  }}
                >
                  {/* TASK COMMMENT */}
                  <TaskComments currentTask={currentTask} currentTaskId={currentTaskId} />
                </Col>
              </Row>
            )}
            {activeTab === '2' && (
              <Row style={{ justifyContent: 'center' }}>
                <Col
                  style={{
                    width: '100%',
                    marginRight: '20px',
                    paddingBottom: '20%',
                  }}
                >
                  <Card
                    title={
                      <Text strong style={{ fontSize: '18px' }}>
                        Related Campaigns
                      </Text>
                    }
                    style={{ borderRadius: '7px', marginTop: '20px' }}
                  >
                    {currentTask && currentTask.relatedCampaigns && currentTask.relatedCampaigns.length > 0 && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: windowWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                          gap: '16px',
                        }}
                      >
                        {currentTask.relatedCampaigns.map((campaign) => (
                          <div
                            key={campaign.id}
                            style={{
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              padding: '16px',
                              transition: 'all 0.2s',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f0f9ff';
                              e.currentTarget.style.borderColor = '#3b82f6';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            onClick={() => window.open(`/campaigns/details/${campaign.id}`, '_blank')}
                          >
                            <div style={{ marginBottom: '12px' }}>
                              <Text
                                strong
                                style={{
                                  fontSize: '16px',
                                  color: '#1e293b',
                                  display: 'block',
                                  marginBottom: '8px',
                                }}
                              >
                                {campaign.name}
                              </Text>
                              {campaign.status && (
                                <Tag color="blue" style={{ marginBottom: '8px' }}>
                                  {campaign.status}
                                </Tag>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>
                              {campaign.startDate && campaign.endDate && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span>📅</span>
                                  <span>
                                    {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            )}
            {activeTab === '3' && (
              <Row>
                <Col style={{ width: windowWidth < 1200 ? '100%' : '48vw', marginRight: windowWidth < 1200 ? '0' : '20px' }}>
                  <Histories histories={histories} loading={loadingHistories} />
                </Col>
              </Row>
            )}
          </Col>

          <Col
            style={{
              width: windowWidth < 1200 ? '100%' : '35%',
              maxWidth: windowWidth < 1200 ? '100%' : '450px',
              marginTop: windowWidth < 1200 ? '0' : '3vh',
            }}
          >
            <Card
              style={{
                borderRadius: '7px',
                height: 'auto',
                border: '1px solid #e5edf4',
                marginBottom: '20px',
              }}
            >
              <Col
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '15px',
                }}
              >
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                  <EyeOutlined
                    style={{
                      paddingRight: '10px',
                      color: '#3b82f6',
                      fontSize: '20px',
                    }}
                  />
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '1px',
                      fontSize: '18px',
                    }}
                  >
                    Followers
                  </Text>
                </Col>
                <Button
                  style={{
                    border: 'none',
                    boxShadow: 'none',
                    marginTop: '-2px',
                  }}
                  disabled={updateTaskLoading}
                  onClick={async () => {
                    const createdBy =
                      currentUser.id ===
                      (currentTask && currentTask.createdBy && currentTask.createdBy.id);

                    if (!isFollowers && (isCurrentUserAssignee || isCollaborator || createdBy)) {
                      if (isCurrentUserAssignee) {
                        message.warning('Assignees already follow tasks by default.');
                      } else if (isCollaborator) {
                        message.warning('Collaborators already follow tasks by default.');
                      } else if (createdBy) {
                        message.warning(
                          'You are the creator of this task and already follows it by default.'
                        );
                      }
                      return;
                    }
                    setIsFollowers(!isFollowers);
                    if (!isFollowers) {
                      try {
                        await updateTask({
                          variables: {
                            data: {
                              followers: {
                                connect: { id: currentUser.id },
                              },
                            },
                            where: { id: currentTaskId },
                          },
                        });
                        refetchTask();
                        message.success('You followed the task.');
                      } catch (error) {
                        message.error('Failed to follow.');
                      }
                    } else {
                      try {
                        await updateTask({
                          variables: {
                            data: {
                              followers: {
                                disconnect: { id: currentUser.id },
                              },
                            },
                            where: { id: currentTaskId },
                          },
                        });
                        refetchTask();
                        message.success('Unfollowed task.');
                      } catch (error) {
                        message.error('Failed to unfollow task.');
                      }
                    }
                  }}
                >
                  {isFollowers ? (
                    <>
                      <MinusOutlined
                        style={{
                          fontSize: '16px',
                          cursor: 'pointer',
                          color: '#F38181',
                          paddingRight: '10px',
                        }}
                      />
                      <Text style={{ color: '#F38181', fontSize: '16px' }}>Unfollow</Text>
                    </>
                  ) : (
                    <>
                      <PlusOutlined
                        style={{
                          fontSize: '16px',
                          cursor: 'pointer',
                          color: '#3b82f6',
                          paddingRight: '10px',
                        }}
                      />
                      <Text style={{ color: '#3b82f6', fontSize: '16px' }}>Follow</Text>
                    </>
                  )}
                </Button>
              </Col>
              <Col>
                {currentTask && currentTask.followers && currentTask.followers.length > 0 ? (
                  <>
                    {currentTask.followers.map((follower, idx) => (
                      <Tooltip
                        key={follower.id || idx}
                        title={startCase(follower.name || follower.username)}
                      >
                        <div
                          style={{
                            display: 'inline-block',
                            marginRight: 8,
                            paddingBottom: '10px',
                          }}
                        >
                          <AvatarS3
                            key={follower.id || idx}
                            imgKey={follower.avatar && follower.avatar.key}
                            username={follower.name || follower.username}
                            avatarProps={{ size: 40 }}
                          />
                        </div>
                      </Tooltip>
                    ))}
                  </>
                ) : (
                  <Card
                    style={{
                      borderRadius: '7px',
                      border: '1px solid #e5edf4',
                    }}
                  >
                    <Text>
                      Followers receive the same alerts and updates as the requestor without
                      official responsibility.
                    </Text>
                  </Card>
                )}
              </Col>
            </Card>

            <Card
              style={{
                borderRadius: '7px',
                height: 'auto',
                border: '1px solid #e5edf4',
                marginBottom: '30px',
              }}
            >
              <div style={{ minHeight: 'fit-content' }}>
                <Row
                  style={{
                    paddingBottom: '15px',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text strong style={{ fontSize: '18px' }}>
                    Details
                  </Text>
                  {currentTask && currentTask.linkUrl && (
                    <Tooltip title={currentTask.linkUrl}>
                      <LinkOutlined
                        style={{
                          fontSize: '16px',
                          pointer: 'cursor',
                          paddingTop: '5px',
                        }}
                        onClick={() => {
                          const url = currentTask?.linkUrl;
                          if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                            window.open(url, '_blank');
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                </Row>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '10px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Urgency
                  </Text>
                  {currentTask && currentTask.urgency ? (
                    <Tag
                      style={{
                        backgroundColor:
                          currentTask && currentTask.urgency
                            ? urgencyColors[currentTask.urgency]
                            : 'gray',
                        borderRadius: '25px',
                        textAlign: 'center',
                        border: 0,
                        width: '100px',
                        padding: '2px',
                        opacity: 0.7,
                      }}
                    >
                      <Row justify="center" type="flex" align="middle" style={{ gap: '10px' }}>
                        <div
                          style={{
                            width: '5px',
                            height: '5px',
                            borderRadius: '50px',
                            backgroundColor:
                              currentTask && currentTask.urgency === 'MEDIUM' ? '#1C1C2D' : 'white',
                          }}
                        />
                        <Text
                          strong
                          style={{
                            fontSize: 12,
                            color:
                              currentTask && currentTask.urgency === 'MEDIUM' ? '#1C1C2D' : 'white',
                          }}
                        >
                          {currentTask && currentTask.urgency
                            ? startCase(currentTask.urgency.toLowerCase())
                            : '-'}
                        </Text>
                      </Row>
                    </Tag>
                  ) : (
                    <Text style={{ fontSize: '14px', color: 'gray' }}>{`-`}</Text>
                  )}
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Start Date
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {currentTask && currentTask.startDate
                      ? new Date(currentTask.startDate).toLocaleDateString()
                      : '-'}
                  </Text>
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Due Date
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {currentTask && currentTask.dueDate
                      ? new Date(currentTask.dueDate).toLocaleDateString()
                      : '-'}
                  </Text>
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Created On
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {currentTask && currentTask.createdAt
                      ? new Date(currentTask.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </Text>
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Requested By
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {startCase(
                      currentTask &&
                        currentTask.createdBy &&
                        (currentTask.createdBy.name || currentTask.createdBy.username || 'Unknown')
                    )}
                  </Text>
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Channel Partner
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {currentTask && currentTask.channelPartner
                      ? currentTask.channelPartner && currentTask.channelPartner.name
                      : '-'}
                  </Text>
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Advertiser
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {currentTask && currentTask.advertiser
                      ? currentTask.advertiser && currentTask.advertiser.name
                      : '-'}
                  </Text>
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '2.5vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Request Type
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {(currentTask && currentTask.requestType) || '-'}
                  </Text>
                </Col>
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '3vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Request Details
                  </Text>
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {currentTask && currentTask.requestSubType ? currentTask.requestSubType : '-'}
                  </Text>
                </Col>
              </div>
            </Card>

            {/* Assignees & Collaborators */}
            <Card
              style={{
                borderRadius: '7px',
                height: 'auto',
                border: '1px solid #e5edf4',
                marginBottom: '80px',
              }}
            >
              <Col
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '15px',
                }}
              >
                <Text
                  strong
                  style={{
                    display: 'block',
                    paddingBottom: '1px',
                    fontSize: '18px',
                  }}
                >
                  Assigned to
                </Text>
                {!isEditingAssignee && level === 'PLATFORM' && (
                  <EditOutlined
                    style={{
                      fontSize: '16px',
                      cursor: 'pointer',
                      paddingTop: '8px',
                    }}
                    onClick={() => setIsEditingAssignee(true)}
                  />
                )}
              </Col>
              {!currentTask?.project && (
                <Col
                  style={{
                    display: 'block',
                    paddingRight: '2.5vw',
                    marginBottom: '15px',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '3px',
                      fontSize: '15px',
                    }}
                  >
                    Assignee Org
                  </Text>
                  {isEditingAssignee ? (
                    <Select
                      className="custom-select"
                      value={selectedOrg}
                      style={{ width: '100%' }}
                      onChange={(value) => {
                        setSelectedOrg(value);
                        setSelectedAssignees([]);
                      }}
                      loading={basicOrgsDetailsLoading}
                      placeholder="Select Organization"
                      onSearch={(e) => fetchBasicOrgs(e)}
                      onFocus={() => fetchBasicOrgs()}
                    >
                      {basicOrgsDetails
                        .concat(
                          currentTask &&
                            currentTask.assigneeOrg &&
                            !basicOrgsDetails.some((org) => org.id === currentTask.assigneeOrg.id)
                            ? [currentTask.assigneeOrg]
                            : []
                        )
                        .map((org) => (
                          <Option key={org.id} value={org.id}>
                            {org.name}
                          </Option>
                        ))}
                    </Select>
                  ) : (
                    <Text style={{ fontSize: '14px', color: 'gray' }}>
                      {currentTask && currentTask.assigneeOrg ? currentTask.assigneeOrg.name : '-'}
                    </Text>
                  )}
                </Col>
              )}
              <Col
                style={{
                  display: 'block',
                  paddingRight: '3vw',
                  overflow: 'hidden',
                }}
              >
                <Text
                  strong
                  style={{
                    display: 'block',
                    paddingBottom: '3px',
                    fontSize: '15px',
                  }}
                >
                  Assignee
                  {currentTask?.project && (
                    <Text style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal', marginLeft: 8 }}>
                      (from project team)
                    </Text>
                  )}
                </Text>
                {isEditingAssignee ? (
                  <Select
                    className="custom-select"
                    value={selectedAssignees}
                    loading={basicUsersDetailsLoading}
                    style={{ width: '100%' }}
                    placeholder={
                      currentTask?.project
                        ? 'Select from project team'
                        : selectedOrg
                        ? 'Select Assignee(s)'
                        : 'Select an organization first'
                    }
                    showSearch
                    allowClear
                    onSearch={(e) => !currentTask?.project && fetchBasicUsers(e)}
                    onChange={(selectedIds) => {
                      const followers = currentTask.followers || [];
                      const followerIds = followers.map((user) => user.id);
                      if (followerIds.includes(selectedIds)) {
                        setShowFollowerModal(true);
                        setSelectedFollowerId(selectedIds);
                      } else {
                        setSelectedAssignees(selectedIds);
                      }
                    }}
                    onFocus={() => !currentTask?.project && fetchBasicUsers()}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    filterSort={(optionA, optionB) => {
                      return optionA.children
                        .toLowerCase()
                        .localeCompare(optionB.children.toLowerCase());
                    }}
                  >
                    {currentTask?.project ? (
                      <>
                        {currentTask.project.owner && (
                          <Option key={currentTask.project.owner.id} value={currentTask.project.owner.id}>
                            {startCase(currentTask.project.owner.name || currentTask.project.owner.username)}
                          </Option>
                        )}
                        {currentTask.project.team &&
                          currentTask.project.team
                            .filter((user) => user.id !== currentTask.project.owner?.id)
                            .map((user) => (
                              <Option key={user.id} value={user.id}>
                                {startCase(user.name || user.username)}
                              </Option>
                            ))}
                      </>
                    ) : (
                      selectedOrg &&
                      basicUsersDetails.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {startCase(user.name || user.username)}
                        </Option>
                      ))
                    )}
                  </Select>
                ) : (
                  <Text style={{ fontSize: '14px', color: 'gray' }}>
                    {currentTask &&
                    currentTask.assigneeUsers &&
                    currentTask.assigneeUsers.length > 0
                      ? currentTask.assigneeUsers.map((user, index) => (
                          <Row
                            key={index}
                            style={{
                              alignItems: 'center',
                              marginBottom: '3px',
                            }}
                          >
                            {startCase(user.name || user.username)}
                          </Row>
                        ))
                      : startCase(
                          currentTask && currentTask.assigneeUsers && currentTask.assigneeUsers.name
                        ) || '-'}
                  </Text>
                )}
              </Col>
              <Col>
                <Divider />
              </Col>
              <Col
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '15px',
                }}
              >
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                  <UserOutlined
                    style={{
                      paddingRight: '10px',
                      color: '#22c55e',
                      fontSize: '20px',
                    }}
                  />
                  <Text
                    strong
                    style={{
                      display: 'block',
                      paddingBottom: '1px',
                      fontSize: '18px',
                    }}
                  >
                    Collaborators
                  </Text>
                </Col>
                <Col>
                  {level === 'PLATFORM' && !isEditingAssignee && (
                    <Button
                      style={{
                        border: 'none',
                        boxShadow: 'none',
                        marginTop: '-2px',
                      }}
                      disabled={updateTaskLoading}
                      onClick={handleCollaboratorsUpdate}
                    >
                      {isCollaborator ? (
                        <>
                          <MinusOutlined
                            style={{
                              fontSize: '16px',
                              cursor: 'pointer',
                              color: '#F38181',
                              paddingRight: '10px',
                            }}
                          />
                          <Text style={{ color: '#F38181', fontSize: '16px' }}>Leave</Text>
                        </>
                      ) : (
                        <>
                          <PlusOutlined
                            style={{
                              fontSize: '16px',
                              cursor: 'pointer',
                              color: '#22c55e',
                              paddingRight: '10px',
                            }}
                          />
                          <Text style={{ color: '#22c55e', fontSize: '16px' }}>Join</Text>
                        </>
                      )}
                    </Button>
                  )}
                </Col>
              </Col>
              {!isEditingAssignee ? (
                <Col>
                  {currentTask &&
                  currentTask.collaborators &&
                  currentTask.collaborators.length > 0 ? (
                    <>
                      {currentTask.collaborators.map((collaborator, idx) => (
                        <Tooltip
                          key={collaborator.id || idx}
                          title={startCase(collaborator.name || collaborator.username)}
                        >
                          <div
                            style={{
                              display: 'inline-block',
                              marginRight: 8,
                              paddingBottom: '10px',
                            }}
                          >
                            <AvatarS3
                              key={collaborator.id || idx}
                              imgKey={collaborator.avatar && collaborator.avatar.key}
                              username={collaborator.name || collaborator.username}
                              avatarProps={{ size: 40 }}
                            />
                          </div>
                        </Tooltip>
                      ))}
                    </>
                  ) : (
                    <Card
                      style={{
                        borderRadius: '7px',
                        border: '1px solid #e5edf4',
                      }}
                    >
                      <Text>
                        Collaborators receive the same alerts as the assignee and can actively help
                        complete the task.
                      </Text>
                    </Card>
                  )}
                </Col>
              ) : (
                <>
                  <Col>
                    <Select
                      className="custom-select"
                      mode="multiple"
                      value={selectedCollaborators}
                      loading={collaboratorsLoading}
                      style={{ width: '100%' }}
                      placeholder="Select collaborator(s)"
                      onSearch={(e) => fetchCollaborators(e)}
                      onChange={(selectedIds) => {
                        setSelectedCollaborators(selectedIds);
                      }}
                      onFocus={() => fetchCollaborators()}
                      filterOption={false}
                      filterSort={(optionA, optionB) => {
                        return optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase());
                      }}
                    >
                      {collaboratorsData.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {startCase(user.name || user.username)}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col>
                    <Divider />
                  </Col>
                  {isEditingAssignee && (
                    <div style={{ marginTop: '10px' }}>
                      <Button
                        type="primary"
                        onClick={handleUserUpdate}
                        disabled={updateTaskLoading}
                        style={{
                          marginLeft: '10px',
                          fontSize: '13px',
                          height: '30px',
                          borderRadius: '5px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                        }}
                      >
                        {updateTaskLoading && <LoadingOutlined />} Save
                      </Button>
                      <Button
                        onClick={() => {
                          setIsEditingAssignee(false);
                          setSelectedOrg(
                            currentTask && currentTask.assigneeOrg && currentTask.assigneeOrg.id
                              ? currentTask.assigneeOrg.id
                              : null
                          );
                          setSelectedAssignees(
                            (currentTask &&
                              currentTask.assigneeUsers &&
                              currentTask.assigneeUsers.map((user) => user.id)) ||
                              []
                          );
                          setSelectedCollaborators(
                            currentTask &&
                              currentTask.collaborators &&
                              currentTask.collaborators.length > 0
                              ? currentTask.collaborators.map((user) => user.id)
                              : []
                          );
                        }}
                        style={{
                          marginLeft: '10px',
                          fontSize: '13px',
                          height: '30px',
                          borderRadius: '5px',
                          color: 'black',
                        }}
                        disabled={updateTaskLoading}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Card>
          </Col>
        </div>
      </PageContentLayout>

      {showFollowerModal && (
        <Modal
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setShowFollowerModal(false)}
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>,
            <Button
              key="ok"
              type="primary"
              disabled={updateTaskLoading}
              onClick={async () => {
                if (showFollowerModal) {
                  try {
                    message.loading('Removing follower...', 0);
                    await updateTask({
                      variables: {
                        where: { id: currentTaskId },
                        data: {
                          followers: {
                            disconnect: [{ id: selectedFollowerId }],
                          },
                        },
                      },
                    });
                    refetchTask();
                    message.destroy();
                    message.success('Follower removed successfully');
                    setShowFollowerModal(false);
                  } catch (error) {
                    message.error(`Failed to remove follower: ${error.message}`);
                  }
                }
              }}
            >
              {updateTaskLoading && <LoadingOutlined />} Remove
            </Button>,
          ]}
          title={'Remove Follower'}
          visible={showFollowerModal}
        >
          <p>
            This user is a follower. Do you want to promote them as an assignee and remove them from
            followers?
          </p>
        </Modal>
      )}
    </div>
  );
};

export default withRouter(TaskDetailsView);
